"use client"

import { useState, useEffect, useCallback } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"

interface CarouselProps {
  images: string[]
  interval?: number
  showControls?: boolean
  className?: string
}

const Carousel = ({ images, interval = 5000, showControls = true, className = "" }: CarouselProps) => {
  const [currentImage, setCurrentImage] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)

  const nextImage = useCallback(() => {
    if (isTransitioning || images.length <= 1) return

    setIsTransitioning(true)
    setCurrentImage((prev) => (prev + 1) % images.length)

    setTimeout(() => {
      setIsTransitioning(false)
    }, 700)
  }, [images.length, isTransitioning])

  const prevImage = useCallback(() => {
    if (isTransitioning || images.length <= 1) return

    setIsTransitioning(true)
    setCurrentImage((prev) => (prev === 0 ? images.length - 1 : prev - 1))

    setTimeout(() => {
      setIsTransitioning(false)
    }, 700)
  }, [images.length, isTransitioning])

  useEffect(() => {
    if (images.length <= 1) return

    const timer = setInterval(() => {
      nextImage()
    }, interval)
    return () => clearInterval(timer)
  }, [interval, nextImage, images.length])

  const handleImageChange = (index: number) => {
    if (isTransitioning || index === currentImage) return
    setIsTransitioning(true)
    setCurrentImage(index)
    setTimeout(() => {
      setIsTransitioning(false)
    }, 700)
  }

  if (!images || images.length === 0) {
    return (
      <div className={`w-full h-full flex items-center justify-center bg-primary ${className}`}>
        <p className="text-white/80">No hay imágenes disponibles</p>
      </div>
    )
  }

  return (
    <div className={`relative w-full h-full overflow-hidden bg-primary flex items-center justify-center ${className}`}>
      {/* Images Container */}
      <div className="relative w-[85%] h-[85%] overflow-hidden rounded-lg">
        {images.map((src, index) => (
          <div
            key={index}
            className={`absolute inset-0 w-full h-full transition-all duration-1000 ease-in-out flex items-center justify-center ${
              index === currentImage ? "opacity-100 scale-100" : "opacity-0 scale-110"
            }`}
            style={{
              zIndex: index === currentImage ? 5 : 0,
            }}
          >
            <img
              src={src || "/placeholder.svg"}
              alt={`Slide ${index + 1}`}
              className="max-w-full max-h-full object-contain"
            />
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      {showControls && images.length > 1 && (
        <>
          <button
            onClick={prevImage}
            className="absolute left-6 top-1/2 -translate-y-1/2 bg-black/20 hover:bg-black/30 text-white rounded-full p-3 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/30 backdrop-blur-sm z-20"
            aria-label="Imagen anterior"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-6 top-1/2 -translate-y-1/2 bg-black/20 hover:bg-black/30 text-white rounded-full p-3 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/30 backdrop-blur-sm z-20"
            aria-label="Siguiente imagen"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      {/* Indicators */}
      {images.length > 1 && (
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-3 z-20">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => handleImageChange(index)}
              className={`transition-all duration-300 rounded-full border-2 border-white/70 focus:outline-none ${
                index === currentImage ? "bg-white w-4 h-4" : "bg-white/30 w-3 h-3 hover:bg-white/50"
              }`}
              aria-label={`Ir a la imagen ${index + 1}`}
              aria-current={index === currentImage ? "true" : "false"}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default Carousel

